import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiFetch, c, DECADES, TOPICS, TOPIC_COLORS } from '../api';

// ── Album design tokens ───────────────────────────────────────────────────────
const T = {
  bg:         '#f4ecd9',
  paper:      '#fdf6e3',
  paperHigh:  '#fffaf0',
  ink:        '#22180e',
  inkSoft:    '#6a5a3e',
  inkMute:    '#a89570',
  accent:     '#c75a2c',
  accentSoft: '#f8dcc1',
  teal:       '#4e8a90',
  tealSoft:   '#cce0e2',
  divider:    'rgba(34,24,14,0.08)',
  display:    "'Newsreader', Georgia, serif",
  body:       "'Work Sans', system-ui, sans-serif",
  radius:     14,
  radiusLg:   22,
};

export default function AdminHome({ user, onLogout, onImpersonate }) {
  const [tab, setTab] = useState('archive');

  useEffect(() => {
    if (!document.getElementById('memories-fonts')) {
      const link = document.createElement('link');
      link.id = 'memories-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300..700;1,6..72,300..700&family=Work+Sans:wght@300..700&display=swap';
      document.head.appendChild(link);
    }
    document.body.style.background = T.bg;
    document.body.style.color = T.ink;
    document.body.style.fontFamily = T.body;
    return () => {
      document.body.style.background = '';
      document.body.style.color = '';
      document.body.style.fontFamily = '';
    };
  }, []);

  const isPlatformAdmin = user?.role === 'admin';
  const tabs = [['archive', 'Archive'], ['index', 'People & Places'], ['facts', 'Facts'], ...(isPlatformAdmin ? [['tree', 'Tree']] : []), ['users', 'Users']];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes weaveBounce { 0%,80%,100%{transform:translateY(0);opacity:0.4} 40%{transform:translateY(-8px);opacity:1} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .adm-input:focus { outline: none; border-color: ${T.accent} !important; box-shadow: 0 0 0 3px ${T.accentSoft} !important; }
        input::placeholder, textarea::placeholder { color: rgba(34,24,14,0.35); }
        select option { background: ${T.paper}; color: ${T.ink}; }
        select option { background: ${T.paper}; color: ${T.ink}; }
      `}</style>

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: `1px solid ${T.divider}`, background: T.paper }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 400, fontStyle: 'italic', color: T.ink, letterSpacing: -0.3 }}>memories</span>
          <span style={{ fontFamily: T.display, fontSize: 22, color: T.accent }}>.</span>
          <span style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.inkMute, marginLeft: 10 }}>Admin</span>
        </div>
        <button onClick={onLogout} style={{ background: 'none', border: 'none', fontFamily: T.body, fontSize: 14, color: T.inkMute, cursor: 'pointer' }}>Sign out</button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${T.divider}`, background: T.paper, overflowX: 'auto' }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex: 1, minWidth: 70, padding: '12px 6px',
            background: 'none', border: 'none',
            borderBottom: tab === key ? `2px solid ${T.accent}` : '2px solid transparent',
            fontFamily: T.body, fontSize: 13, fontWeight: tab === key ? 600 : 400,
            color: tab === key ? T.accent : T.inkMute,
            cursor: 'pointer', whiteSpace: 'nowrap',
            transition: 'color 0.15s',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '20px 20px', maxWidth: 860, margin: '0 auto', width: '100%', flex: 1 }}>
        {tab === 'archive'  && <ArchiveTab currentUser={user} />}
        {tab === 'index'    && <IndexTab />}
        {tab === 'facts'    && <FactsTab />}
        {tab === 'tree'     && <TreeTab />}
        {tab === 'users'    && <UsersTab onImpersonate={onImpersonate} />}
      </div>
    </div>
  );
}

// ─── NEW Badge ────────────────────────────────────────────────────────────────

function NewBadge() {
  return (
    <span style={{ background: T.accent, color: T.paper, fontFamily: T.body, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase', flexShrink: 0 }}>
      New
    </span>
  );
}

// ─── Weaving Loader ───────────────────────────────────────────────────────────

const WEAVE_MESSAGES = ['Reading memories…', 'Finding the threads…', 'Weaving the story…', 'Adding warmth…', 'Almost there…'];

function WeavingLoader({ messages }) {
  const msgs = messages || WEAVE_MESSAGES;
  const [msgIndex, setMsgIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setMsgIndex(i => (i + 1) % msgs.length), 2200);
    return () => clearInterval(interval);
  }, [msgs]);
  return (
    <div style={s.weavingBox}>
      <div style={s.weavingSpinner}>
        {[0,1,2,3,4].map(i => <div key={i} style={{ ...s.weavingDot, animationDelay: `${i * 0.15}s` }} />)}
      </div>
      <div style={s.weavingMsg}>{msgs[msgIndex]}</div>
      <style>{`@keyframes weaveBounce { 0%,80%,100%{transform:translateY(0);opacity:0.4} 40%{transform:translateY(-8px);opacity:1} }`}</style>
    </div>
  );
}

// ─── Narrative Display ────────────────────────────────────────────────────────

function NarrativeDisplay({ raw, onDismiss }) {
  const cleaned = raw.replace(/^#+\s*/gm, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/^---+$/gm, '').trim();
  const gapsSplit = cleaned.split(/\nGAPS:\s*/i);
  const narrative = gapsSplit[0].trim();
  const gaps = (gapsSplit[1] || '').split('\n').map(g => g.replace(/^[-•*\d.]+\s*/, '').trim()).filter(Boolean);
  return (
    <div style={s.aggregationBox}>
      <div style={s.aggregationLabel}>✦ AI NARRATIVE</div>
      <div style={s.aggregationText}>
        {narrative.split('\n').filter(Boolean).map((para, i) => (
          <p key={i} style={{ margin: '0 0 16px 0', lineHeight: 1.8 }}>{para}</p>
        ))}
      </div>
      {gaps.length > 0 && (
        <div style={s.gapsBox}>
          <div style={s.gapsLabel}>QUESTIONS TO EXPLORE NEXT</div>
          {gaps.map((g, i) => (
            <div key={i} style={s.gapItem}>
              <span style={s.gapNum}>{i + 1}</span>
              <span style={s.gapText}>{g}</span>
            </div>
          ))}
        </div>
      )}
      <button style={s.clearAgg} onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

// ─── Admin Text Entry ─────────────────────────────────────────────────────────

function AdminTextEntry({ storytellerId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await apiFetch('/memories/text', {
        method: 'POST',
        body: JSON.stringify({ text: text.trim(), user_id: storytellerId, recorded_at: new Date().toISOString() }),
      });
      setText(''); setOpen(false); onSaved();
      alert('Memory saved and being tagged in the background.');
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };

  if (!open) return (
    <button style={{ ...s.aggregateBtn, marginBottom: 12, width: '100%', textAlign: 'center' }} onClick={() => setOpen(true)}>
      ✍️ Add written memory for this storyteller
    </button>
  );

  return (
    <div style={{ background: T.paperHighHigh, border: `1px solid ${T.divider}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.accent, marginBottom: 10 }}>✍️ Written Memory</div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Type or paste the memory here… (a letter, a story, a document)"
        style={{ width: '100%', minHeight: 160, background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: 10, color: T.ink, fontSize: 15, lineHeight: 1.7, padding: 12, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button style={s.saveBtn} onClick={submit} disabled={saving || !text.trim()}>{saving ? 'Saving…' : 'Save Memory'}</button>
        <button style={s.cancelBtn} onClick={() => { setOpen(false); setText(''); }}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Archive Tab ──────────────────────────────────────────────────────────────

function ArchiveTab({ currentUser }) {
  const [memories, setMemories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [decade, setDecade] = useState('All');
  const [topic, setTopic] = useState('All');
  const [storyteller, setStoryteller] = useState('All');
  const [selected, setSelected] = useState(null);
  const [aggregating, setAggregating] = useState(false);
  const [aggregation, setAggregation] = useState(null);

  useEffect(() => { apiFetch('/admin/users').then(setUsers).catch(() => {}); }, []);

  const fetchMemories = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (decade !== 'All') params.set('decade', decade);
    if (topic !== 'All') params.set('topic', topic);
    if (storyteller !== 'All') params.set('user_id', storyteller);
    const data = await apiFetch(`/memories?${params}`).catch(() => []);
    setMemories(data);
    setLoading(false);
  }, [search, decade, topic, storyteller]);

  useEffect(() => { fetchMemories(); }, [fetchMemories]);

  // Update a single memory in the list (called from MemoryDetail on mark-as-read)
  const handleUpdateMemory = (updated) => {
    setMemories(prev => prev.map(m => m.id === updated.id ? updated : m));
    setSelected(updated);
  };

  const aggregate = async () => {
    if (storyteller === 'All' && !search.trim()) return alert('Please select a storyteller or type a search term first.');
    setAggregating(true); setAggregation(null);
    try {
      const body = {};
      if (storyteller !== 'All') body.user_id = storyteller;
      if (search.trim()) body.query = search.trim();
      const res = await apiFetch('/memories/aggregate', { method: 'POST', body: JSON.stringify(body) });
      setAggregation(res.narrative);
    } catch (e) { alert('Aggregation failed: ' + e.message); }
    setAggregating(false);
  };

  if (selected) return (
    <MemoryDetail
      memory={selected}
      onBack={() => setSelected(null)}
      onDelete={() => { setSelected(null); fetchMemories(); }}
      onUpdate={handleUpdateMemory}
    />
  );

  return (
    <>
      <div style={{ position: 'sticky', top: 0, background: T.bg, zIndex: 10, paddingBottom: 8 }}>
      <div style={s.filters}>
        <input style={s.searchInput} placeholder="Search memories, people, places…" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={s.filterRow}>
          <select style={s.select} value={decade} onChange={e => setDecade(e.target.value)}>
            <option>All</option>
            {DECADES.map(d => <option key={d}>{d}</option>)}
          </select>
          <select style={s.select} value={topic} onChange={e => setTopic(e.target.value)}>
            <option>All</option>
            {TOPICS.map(t => <option key={t}>{t}</option>)}
          </select>
          <select style={s.select} value={storyteller} onChange={e => setStoryteller(e.target.value)}>
            <option value="All">All storytellers</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.aggregateBtn} onClick={aggregate} disabled={aggregating}>
            {aggregating ? '⏳ Weaving…' : '✦ Weave Story'}
          </button>
          {(search || decade !== 'All' || topic !== 'All' || storyteller !== 'All') && (
            <button style={{ ...s.aggregateBtn, background: '#fdf0ee', color: T.accent }} onClick={() => { setSearch(''); setDecade('All'); setTopic('All'); setStoryteller('All'); }}>
              ✕ Clear
            </button>
          )}
        </div>
      </div>
      </div>{/* end sticky */}
      {aggregating && <WeavingLoader />}
      {!aggregating && aggregation && <NarrativeDisplay raw={aggregation} onDismiss={() => setAggregation(null)} />}
      {storyteller !== 'All' && <AdminTextEntry storytellerId={storyteller} onSaved={fetchMemories} />}
      <div style={s.count}>{loading ? 'Loading…' : `${memories.length} memor${memories.length === 1 ? 'y' : 'ies'}`}</div>
      {!loading && memories.length === 0
        ? <p style={{ fontSize: 14, color: T.inkTert, textAlign: 'center', padding: 20 }}>No recordings found.</p>
        : (
          <div style={s.list}>
            {memories.map(m => (
              <button key={m.id} style={s.card} onClick={() => setSelected(m)}>
                <div style={s.cardMeta}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={s.cardDecade}>{m.decade}</span>
                    {m.is_new && <NewBadge />}
                    {m.rating === 'R' && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.accent, border: `1px solid ${T.accent}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>R</span>}
                    {m.rating === 'P' && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.inkMute, border: `1px solid ${T.inkMute}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>PRIVATE</span>}
                    {m.is_prompted === 1 && m.suggested_prompt_id && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.teal, border: `1px solid ${T.teal}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>✦ Prompted</span>}
                    {m.is_prompted === 1 && !m.suggested_prompt_id && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.inkMute, border: `1px solid ${T.inkMute}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>AI Prompted</span>}
                    {m.visibility === 'private' && !m.rating && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.teal, border: `1px solid ${T.teal}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>PRIVATE</span>}
                  </div>
                  <span style={s.cardDate}>{new Date(m.recorded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                </div>
                <div style={s.cardTitle}>{m.title}</div>
                {m.location_name && <div style={s.cardLoc}>📍 {m.location_name}</div>}
                <div style={s.cardSummary}>{m.summary}</div>
                <div style={s.cardTags}>
                  {m.topics && m.topics.slice(0, 3).map(t => (
                    <span key={t} style={{ ...s.tag }}>{t}</span>
                  ))}
                </div>
              {m.is_new && (
                <div onClick={e => { e.stopPropagation(); apiFetch(`/memories/${m.id}/review`, { method: 'POST' }).then(() => setMemories(prev => prev.map(x => x.id === m.id ? { ...x, is_new: false } : x))).catch(() => {}); }} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: `1px solid ${T.divider}`, borderRadius: 99, padding: '4px 12px', cursor: 'pointer' }}>
                  <span style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute }}>✓ Mark as read</span>
                </div>
              )}
              </button>
            ))}
          </div>
        )
      }
    </>
  );
}

// ─── Memory Detail (Admin) ────────────────────────────────────────────────────

// ─── Memory Photos (Admin) ────────────────────────────────────────────────────

function MemoryPhotos({ memoryId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urls, setUrls] = useState({});
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/photos/memory/${memoryId}`);
      setPhotos(data);
      const token = localStorage.getItem('memories_token');
      const next = {};
      await Promise.all(data.map(async p => {
        try {
          const res = await fetch(p.url, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.ok) next[p.id] = URL.createObjectURL(await res.blob());
        } catch {}
      }));
      setUrls(next);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [memoryId]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const token = localStorage.getItem('memories_token');
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('memory_id', memoryId);
        const res = await fetch('/api/photos/upload', {
          method: 'POST', body: fd, headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Upload failed');
        }
      } catch (err) { alert('Could not add ' + file.name + ': ' + err.message); }
    }
    await load();
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = async (id) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await apiFetch(`/photos/${id}`, { method: 'DELETE' });
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch (err) { alert('Could not remove: ' + err.message); }
  };

  return (
    <div style={{ background: T.paperHigh, borderRadius: T.radius, padding: '14px 16px', marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: T.accent, fontWeight: 700, letterSpacing: '0.1em' }}>PHOTOS</div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', padding: 0, fontWeight: 600 }}>
          {uploading ? 'Adding…' : '+ Add photos'}
        </button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFile} style={{ display: 'none' }} />

      {loading ? (
        <div style={{ fontSize: 14, color: T.inkMute }}>Loading…</div>
      ) : photos.length === 0 ? (
        <div style={{ fontSize: 14, color: T.inkMute, lineHeight: 1.5 }}>No photos attached yet.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {photos.map(p => (
            <div key={p.id} style={{ position: 'relative' }}>
              <button onClick={() => urls[p.id] && setViewing(urls[p.id])} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                {urls[p.id] ? (
                  <img src={urls[p.id]} alt={p.caption || 'Memory photo'} style={{ width: 104, height: 104, objectFit: 'cover', borderRadius: 10, border: `1px solid ${T.divider}`, display: 'block' }} />
                ) : (
                  <div style={{ width: 104, height: 104, borderRadius: 10, background: T.paper, border: `1px solid ${T.divider}` }} />
                )}
              </button>
              <button onClick={() => removePhoto(p.id)} style={{ position: 'absolute', top: -6, right: -6, width: 24, height: 24, borderRadius: '50%', background: T.paper, border: `1px solid ${T.divider}`, color: T.inkMute, fontSize: 15, lineHeight: 1, padding: 0, cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div onClick={() => setViewing(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(34,24,14,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, cursor: 'pointer' }}>
          <img src={viewing} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12 }} />
        </div>
      )}
    </div>
  );
}

function MemoryDetail({ memory: initialMemory, onBack, onDelete, onUpdate }) {
  const [memory, setMemory] = React.useState(initialMemory);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  // Only changes when user explicitly clicks Mark as Read — no auto-mark
  const [memoryIsNew, setMemoryIsNew] = useState(memory.is_new);
  const [showPrompt, setShowPrompt] = useState(false);

  // Fetch audio with auth header → blob URL
  useEffect(() => {
    if (!memory.audio_path) return;
    const token = localStorage.getItem('memories_token');
    setAudioUrl(null); setAudioError(false);
    fetch(`/api/memories/${memory.id}/audio`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(res.status); return res.blob(); })
      .then(blob => setAudioUrl(URL.createObjectURL(blob)))
      .catch(e => { console.error('Audio fetch error:', e); setAudioError(true); });
  }, [memory.id]);

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  // Mark as read — only fires when user taps the button
  const markAsRead = async () => {
    try {
      await apiFetch(`/memories/${memory.id}/review`, { method: 'POST' });
      setMemoryIsNew(false);
      if (onUpdate) onUpdate({ ...memory, is_new: false });
    } catch (e) { alert('Could not mark as read: ' + e.message); }
  };

  // Re-run AI tagging on the existing transcript
  const reprocess = async () => {
    if (!window.confirm('Re-run AI tagging? The title, summary, topics, people and places will be regenerated from the transcript.')) return;
    setReprocessing(true);
    try {
      const updated = await apiFetch(`/memories/${memory.id}/reprocess`, { method: 'POST' });
      if (onUpdate) onUpdate({ ...updated, is_new: memoryIsNew });
      alert('Done! Tags, title and summary have been updated.');
    } catch (e) { alert('Reprocess failed: ' + e.message); }
    setReprocessing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this memory? This cannot be undone.')) return;
    await apiFetch(`/memories/${memory.id}`, { method: 'DELETE' });
    onDelete();
  };

  return (
    <div>
      <button style={s.backBtn} onClick={onBack}>← Back</button>
      <div style={s.detailCard}>

       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={s.cardDecade}>{memory.decade}</div>
          {memory.rating === 'R' && <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, border: '1px solid #e07070', borderRadius: 4, padding: '1px 6px' }}>R</span>}
          {memory.rating === 'P' && <span style={{ fontSize: 11, fontWeight: 700, color: '#a070e0', border: '1px solid #a070e0', borderRadius: 4, padding: '1px 6px' }}>PRIVATE</span>}
        </div>

        {/* Title + NEW badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <h2 style={{ ...s.detailTitle, margin: 0, flex: 1 }}>{memory.title}</h2>
          {memoryIsNew && <NewBadge />}
        </div>

        {/* Rating + Visibility controls */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <select
            value={memory.rating || ''}
            onChange={async e => {
              const updated = { ...memory, rating: e.target.value || null };
              await apiFetch(`/memories/${memory.id}`, { method: 'PATCH', body: JSON.stringify({ rating: e.target.value || null }) });
              setMemory(updated);
              if (onUpdate) onUpdate(updated);
            }}
            style={{ background: '#2c2c2e', border: '1px solid #38383a', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
          >
            <option value="">No Rating</option>
            <option value="R">R — Adults only</option>
            <option value="P">P — Private (storyteller only)</option>
          </select>
          <select
            value={memory.visibility || 'everyone'}
            onChange={async e => {
              await apiFetch(`/memories/${memory.id}`, { method: 'PATCH', body: JSON.stringify({ visibility: e.target.value }) });
              const updatedVis = { ...memory, visibility: e.target.value };
              setMemory(updatedVis);
              if (onUpdate) onUpdate(updatedVis);
            }}
            style={{ background: '#2c2c2e', border: '1px solid #38383a', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
          >
            <option value="everyone">Visible to everyone</option>
            <option value="family">My family group only</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div style={s.detailMeta}>
          <span>{new Date(memory.recorded_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          {memory.location_name && <span>📍 {memory.location_name}</span>}
        </div>

        {audioUrl && (
          <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} style={{ display: 'none' }} />
        )}

        <button
          style={{ ...s.playBtn, opacity: (audioUrl || audioError) ? 1 : 0.5, background: audioError ? '#3a1a1a' : T.accent, color: audioError ? '#e07070' : T.ink }}
          onClick={togglePlay}
          disabled={!audioUrl}
        >
          {audioError ? '⚠ Audio unavailable' : !audioUrl && memory.audio_path ? '⏳ Loading…' : playing ? '⏸ Pause' : '▶ Play Recording'}
        </button>

        {/* Action row — always visible */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {memoryIsNew && (
            <button onClick={markAsRead} style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.inkTert, fontSize: 13, padding: '8px 0', borderRadius: 10, cursor: 'pointer' }}>
              ✓ Mark as read
            </button>
          )}
          <button onClick={reprocess} disabled={reprocessing} style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.accent, fontSize: 13, padding: '8px 0', borderRadius: 10, cursor: 'pointer' }}>
            {reprocessing ? '⏳ Retagging…' : '🔄 Re-tag'}
          </button>
        </div>

        {memory.people?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={s.detailSectionLabel}>People</div>
            <div style={s.cardTags}>{memory.people.map(p => <span key={p} style={s.tag}>{p}</span>)}</div>
          </div>
        )}
        {memory.topics?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={s.detailSectionLabel}>Topics</div>
            <div style={s.cardTags}>{memory.topics.map(t => <span key={t} style={{ ...s.tag }}>{t}</span>)}</div>
          </div>
        )}
        {memory.prompt_text && (
          <div style={{ marginBottom: 8 }}>
            <button
              onClick={() => setShowPrompt(p => !p)}
              style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ fontSize: 11 }}>{showPrompt ? '▾' : '▸'}</span>
              {showPrompt ? 'Hide prompt' : 'Show prompt'}
            </button>
            {showPrompt && (
              <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', marginTop: 8 }}>
                <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 8px' }}>Prompt</p>
                <p style={{ fontFamily: T.display, fontSize: 16, fontStyle: 'italic', color: T.inkSoft, lineHeight: 1.65, margin: 0 }}>{memory.prompt_text}</p>
              </div>
            )}
          </div>
        )}
        <MemoryPhotos memoryId={memory.id} />
        <TranscriptEditor memory={memory} onUpdate={onUpdate} />
        <button style={s.deleteBtn} onClick={handleDelete}>Delete Memory</button>
      </div>
    </div>
  );
}

// ─── Transcript Editor ───────────────────────────────────────────────────────

function TranscriptEditor({ memory, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(memory.transcript || '');
  const [saving, setSaving] = useState(false);

  const label = memory.audio_path ? 'TRANSCRIPT' : 'WRITTEN MEMORY';

  const save = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/memories/${memory.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ transcript: draft }),
      });
      if (onUpdate) onUpdate({ ...memory, transcript: draft });
      setEditing(false);
    } catch (e) { alert('Could not save: ' + e.message); }
    setSaving(false);
  };

  return (
    <div style={{ background: T.paperHigh, borderRadius: T.radius, padding: '14px 16px', marginTop: 16, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: T.accent, fontWeight: 700, letterSpacing: '0.1em' }}>{label}</div>
        {!editing && (
          <button style={{ background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', padding: 0 }} onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            style={{ width: '100%', minHeight: 200, background: T.paper, border: `1px solid ${T.divider}`, borderRadius: 10, color: T.ink, fontSize: 15, lineHeight: 1.7, padding: 12, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={{ flex: 1, background: T.accent, border: 'none', color: '#000', padding: '10px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.ink, padding: '10px', borderRadius: 10, fontSize: 15, cursor: 'pointer' }} onClick={() => { setDraft(memory.transcript || ''); setEditing(false); }}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <p style={{ fontSize: 15, color: memory.transcript ? T.ink : T.inkTert, margin: 0, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
          {memory.transcript || 'No transcript yet.'}
        </p>
      )}
    </div>
  );
}

// ─── Index Tab ────────────────────────────────────────────────────────────────

function IndexTab() {
  const [activeTab, setActiveTab] = useState('people');
  const [mode, setMode] = useState('by_storyteller'); // by_storyteller | about_person
  const [people, setPeople] = useState([]);
  const [places, setPlaces] = useState([]);
  const [allPeople, setAllPeople] = useState([]); // across all storytellers
  const [allPlaces, setAllPlaces] = useState([]); // across all storytellers
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemMode, setSelectedItemMode] = useState('by_storyteller');
  const [itemMemories, setItemMemories] = useState([]);
  const [itemNarrative, setItemNarrative] = useState('');
  const [loadingItem, setLoadingItem] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [search, setSearch] = useState('');
  const [facts, setFacts] = useState([]);

  // Load facts for nickname matching
  useEffect(() => {
    apiFetch('/admin/facts').then(setFacts).catch(() => {});
  }, []);

  // Build alias pairs from facts — only link names that are explicitly aliases
  const aliasMap = React.useMemo(() => {
    const map = {};
    const addAlias = (a, b) => {
      const ka = a.toLowerCase().trim();
      const kb = b.toLowerCase().trim();
      if (!ka || !kb || ka === kb) return;
      if (!map[ka]) map[ka] = new Set();
      if (!map[kb]) map[kb] = new Set();
      map[ka].add(kb);
      map[kb].add(ka);
    };
    facts.forEach(f => {
      const text = f.cleaned_text || f.raw_text || '';
      // Pattern: "X is also known as Y", "X goes by Y", "X is called Y"
      let m;
      m = text.match(/^(.+?)\s+(?:is also known as|goes by|is called|is commonly called|has the nickname|is nicknamed)\s+["']?(.+?)["']?\.?$/i);
      if (m) { addAlias(m[1].trim(), m[2].trim()); return; }
      // Pattern: "X and Y are the same person"
      m = text.match(/^(.+?)\s+and\s+(.+?)\s+are the same person/i);
      if (m) { addAlias(m[1].trim(), m[2].trim()); return; }
      // Pattern: "X's nickname is Y" or "X gave Y the nickname Z"
      m = text.match(/nickname\s+(?:for\s+)?(.+?)\s+is\s+["']?(.+?)["']?\.?$/i);
      if (m) { addAlias(m[1].trim(), m[2].trim()); return; }
      // Pattern: "X (Y) is..." — parenthetical alias
      m = text.match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+\(([^)]+)\)/);
      if (m) { addAlias(m[1].trim(), m[2].trim()); return; }
      // Pattern: "X and Y are spelling variations"
      m = text.match(/^(.+?)\s+and\s+(.+?)\s+are\s+(?:spelling\s+)?variations/i);
      if (m) { addAlias(m[1].trim(), m[2].trim()); }
    });
    return map;
  }, [facts]);

  const matchesSearch = (itemName) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    const name = itemName.toLowerCase();
    // Direct match
    if (name.includes(q)) return true;
    // Check if any alias of this name matches the query
    const aliases = aliasMap[name] || new Set();
    for (const alias of aliases) {
      if (alias.includes(q)) return true;
    }
    // Check partial name matches (e.g. "Gil" matches "Mario Gil Luera")
    const nameParts = name.split(' ');
    for (const part of nameParts) {
      if (part.length > 2 && part.includes(q)) return true;
      const partAliases = aliasMap[part] || new Set();
      for (const alias of partAliases) {
        if (alias.includes(q)) return true;
      }
    }
    // Reverse: check if query is an alias that maps to this name
    for (const [key, aliasSet] of Object.entries(aliasMap)) {
      if (key.includes(q)) {
        if (name.includes(key) || aliasSet.has(name)) return true;
        for (const alias of aliasSet) {
          if (name.includes(alias)) return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    apiFetch('/admin/users').then(u => { setUsers(u); if (u.length > 0) setSelectedUser(u[0].id); }).catch(() => {});
  }, []);

  // Load per-storyteller index
  useEffect(() => {
    if (!selectedUser || mode !== 'by_storyteller') return;
    const userParam = selectedUser !== 'all' ? `?user_id=${selectedUser}` : '';
    apiFetch(`/memories/index/people${userParam}`).then(setPeople).catch(() => setPeople([]));
    apiFetch(`/memories/index/places${userParam}`).then(setPlaces).catch(() => setPlaces([]));
  }, [selectedUser, mode]);

  // Load cross-family index (all storytellers)
  useEffect(() => {
    if (mode !== 'about_person') return;
    apiFetch('/memories/index/people').then(setAllPeople).catch(() => setAllPeople([]));
    apiFetch('/memories/index/places').then(setAllPlaces).catch(() => setAllPlaces([]));
  }, [mode]);

  const openItem = async (name, itemMode) => {
    setSelectedItem(name); setSelectedItemMode(itemMode);
    setLoadingItem(true); setItemMemories([]); setItemNarrative('');
    try {
      if (itemMode === 'by_storyteller') {
        const mems = await apiFetch(`/memories?${new URLSearchParams({ search: name, user_id: selectedUser })}`);
        setItemMemories(mems);
      } else {
        const mems = await apiFetch(`/memories?${new URLSearchParams({ search: name })}`);
        setItemMemories(mems);
      }
    } catch (e) { console.error(e); }
    setLoadingItem(false);
  };

  if (selectedMemory) return <MemoryDetail memory={selectedMemory} onBack={() => setSelectedMemory(null)} onDelete={() => setSelectedMemory(null)} />;

  const runBiographyWeave = async () => {
    setLoadingItem(true); setItemNarrative('');
    try {
      const body = selectedItemMode === 'by_storyteller'
        ? { query: selectedItem, user_id: selectedUser }
        : { query: selectedItem };
      const res = await apiFetch('/memories/aggregate', { method: 'POST', body: JSON.stringify(body) });
      setItemNarrative(res.narrative);
    } catch (e) { alert('Weave failed: ' + e.message); }
    setLoadingItem(false);
  };

  if (selectedItem) return (
    <div>
      <button style={s.backBtn} onClick={() => setSelectedItem(null)}>← Back</button>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontFamily: T.display, fontSize: 26, fontWeight: 400, color: T.ink, margin: '0 0 4px', letterSpacing: -0.3 }}>{selectedItem}</h2>
        <p style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, margin: 0 }}>
          {selectedItemMode === 'by_storyteller'
            ? `Stories by ${users.find(u => u.id === selectedUser)?.nickname || users.find(u => u.id === selectedUser)?.name?.split(' ')[0] || 'this storyteller'} mentioning ${selectedItem}`
            : `All family stories mentioning ${selectedItem}`
          }
        </p>
      </div>

      {/* Weave button */}
      {!itemNarrative && !loadingItem && itemMemories.length > 0 && (
        <button onClick={runBiographyWeave} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: T.ink, border: 'none', color: T.paper,
          padding: '14px 20px', borderRadius: T.radiusLg,
          fontFamily: T.body, fontSize: 15, fontWeight: 600,
          cursor: 'pointer', marginBottom: 16, width: '100%',
          justifyContent: 'center',
        }}>
          ✦ Weave {selectedItemMode === 'about_person' ? 'their story from all perspectives' : 'these stories together'}
        </button>
      )}

      {loadingItem && <WeavingLoader />}
      {itemNarrative && <NarrativeDisplay raw={itemNarrative} onDismiss={() => setItemNarrative('')} />}

      <div style={s.list}>
        {itemMemories.length === 0
          ? <p style={s.centerMsg}>No memories found mentioning {selectedItem}</p>
          : itemMemories.map(m => (
            <button key={m.id} style={s.card} onClick={() => setSelectedMemory(m)}>
              <div style={s.cardMeta}>
                <span style={s.cardDecade}>{m.decade}</span>
                <span style={s.cardDate}>{new Date(m.recorded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
              </div>
              <div style={s.cardTitle}>{m.title}</div>
              {m.location_name && <div style={s.cardLoc}>📍 {m.location_name}</div>}
              <div style={s.cardSummary}>{m.summary}</div>
            </button>
          ))
        }
      </div>
    </div>
  );

  // Mode: by storyteller
  const storyItems = activeTab === 'people' ? people : places;
  // Mode: about person (all storytellers)
  const allItems = activeTab === 'people' ? allPeople : allPlaces;

  return (
    <div>
      {/* Mode toggle */}
      <div style={{ position: 'sticky', top: 0, background: T.bg, zIndex: 10, paddingTop: 4, paddingBottom: 4 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button onClick={() => setMode('by_storyteller')} style={{
          flex: 1, padding: '10px', borderRadius: T.radius,
          background: mode === 'by_storyteller' ? T.ink : T.paperHigh,
          color: mode === 'by_storyteller' ? T.paper : T.inkSoft,
          border: `1px solid ${T.divider}`, fontFamily: T.body, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          Stories by a storyteller
        </button>
        <button onClick={() => setMode('about_person')} style={{
          flex: 1, padding: '10px', borderRadius: T.radius,
          background: mode === 'about_person' ? T.ink : T.paperHigh,
          color: mode === 'about_person' ? T.paper : T.inkSoft,
          border: `1px solid ${T.divider}`, fontFamily: T.body, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          Stories about a person/place
        </button>
      </div>

      {/* By storyteller mode */}
      {mode === 'by_storyteller' && (
        <div style={{ marginBottom: 14 }}>
          <select style={{ ...s.select, width: '100%', marginBottom: 12 }} value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
            {users.map(u => <option key={u.id} value={u.id}>{u.nickname || u.name}</option>)}
          </select>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, margin: '0 0 10px' }}>
            People and places mentioned in their memories.
          </p>
        </div>
      )}

      {/* About person mode */}
      {mode === 'about_person' && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.paperHigh, border: `1.5px solid ${T.divider}`, borderRadius: T.radiusLg, padding: '10px 14px', marginBottom: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.inkMute} strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="6"/><path d="M20 20l-4.5-4.5"/></svg>
            <input
              className="adm-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or nickname…"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: T.body, fontSize: 15, color: T.ink }}
            />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: T.inkMute, cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>}
          </div>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, margin: 0 }}>
            All family stories mentioning this person or place — across every storyteller.
          </p>
        </div>
      )}

      {/* People / Places toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button style={{ ...s.indexTab, ...(activeTab === 'people' ? s.indexTabActive : {}) }} onClick={() => setActiveTab('people')}>
          👤 People ({mode === 'by_storyteller' ? people.length : allPeople.length})
        </button>
        <button style={{ ...s.indexTab, ...(activeTab === 'places' ? s.indexTabActive : {}) }} onClick={() => setActiveTab('places')}>
          📍 Places ({mode === 'by_storyteller' ? places.length : allPlaces.length})
        </button>
      </div>
      </div>{/* end sticky */}

      <div style={s.indexList}>
        {(mode === 'by_storyteller' ? storyItems : allItems)
          .filter(item => mode === 'about_person' ? matchesSearch(item.name) : true)
          .map(item => (
            <button key={item.name} style={s.indexItem} onClick={() => openItem(item.name, mode)}>
              <span style={s.indexName}>{item.name}</span>
              <span style={s.indexCount}>{item.count} {item.count === 1 ? 'memory' : 'memories'} →</span>
            </button>
          ))
        }
        {(mode === 'by_storyteller' ? storyItems : allItems)
          .filter(item => mode === 'about_person' ? matchesSearch(item.name) : true)
          .length === 0 && (
          <p style={s.centerMsg}>{search ? `No results for "${search}"` : 'No data yet'}</p>
        )}
      </div>
    </div>
  );
}

// ─── Facts Tab ────────────────────────────────────────────────────────────────

function FactsTab() {
  const [facts, setFacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [newFact, setNewFact] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    apiFetch('/admin/users').then(u => { setUsers(u); setSelectedUser('all'); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedUser) return;
    setLoading(true);
    apiFetch('/admin/facts').then(f => { setFacts(f); setLoading(false); }).catch(() => setLoading(false));
  }, [selectedUser]);
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [retagging, setRetagging] = useState(false);
  const [retagProgress, setRetagProgress] = useState('');
  const [rebuildingAliases, setRebuildingAliases] = useState(false);

  const analyze = async () => {
    setAnalyzing(true); setSuggestions([]);
    try {
      const res = await apiFetch('/admin/facts/analyze', { method: 'POST' });
      setSuggestions(res.suggestions || []);
      if (!res.suggestions?.length) alert('No suggestions found. Add more facts or record more memories first.');
    } catch (e) { alert('Analysis failed: ' + e.message); }
    setAnalyzing(false);
  };
  

  const addFact = async () => {
    if (!newFact.trim()) return;
    setAdding(true);
    try {
      await apiFetch('/admin/facts', { method: 'POST', body: JSON.stringify({ raw_text: newFact.trim() }) });
      setNewFact('');
      setFacts(await apiFetch('/admin/facts'));
    } catch (e) { alert(e.message); }
    setAdding(false);
  };

  const deleteFact = async (id) => {
    await apiFetch(`/admin/facts/${id}`, { method: 'DELETE' });
    setFacts(f => f.filter(x => x.id !== id));
  };

  return (
    <div>
      <div style={{ position: 'sticky', top: 0, background: T.bg, zIndex: 10, paddingBottom: 8 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={{ ...s.searchInput, fontSize: 14, padding: '10px 14px' }}
            placeholder="Add a family fact… (e.g. Gil is Abraham's brother)"
            value={newFact}
            onChange={e => setNewFact(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addFact()}
          />
          <button style={s.aggregateBtn} onClick={addFact} disabled={adding}>{adding ? '…' : 'Add'}</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <button style={{ ...s.aggregateBtn, flex: 1, textAlign: 'center' }} onClick={analyze} disabled={analyzing}>
            {analyzing ? '⏳ Analyzing…' : '✦ Analyze tag issues'}
          </button>
          <button style={{ ...s.aggregateBtn, flex: 1, textAlign: 'center', background: rebuildingAliases ? 'rgba(34,24,14,0.15)' : T.teal, color: rebuildingAliases ? T.inkMute : T.paper }}
            onClick={async () => {
              setRebuildingAliases(true);
              try {
                const res = await apiFetch('/admin/aliases/rebuild', { method: 'POST' });
                alert('✓ Rebuilt ' + res.count + ' alias pairs. Re-tag all memories to apply.');
              } catch (e) { alert('Failed: ' + e.message); }
              setRebuildingAliases(false);
            }} disabled={rebuildingAliases}>
            {rebuildingAliases ? '⏳ Rebuilding…' : '⟳ Rebuild aliases'}
          </button>
          <button style={{ ...s.aggregateBtn, flex: 1, textAlign: 'center', background: retagging ? 'rgba(34,24,14,0.15)' : '#1a3a1a', color: retagging ? T.inkMute : '#80c090' }}
            onClick={async () => {
              if (!window.confirm('Re-tag ALL memories using current facts? This may take a few minutes.')) return;
              setRetagging(true); setRetagProgress('Starting…');
              try {
                const res = await apiFetch('/memories/reprocess-all', { method: 'POST' });
                const total = res.count || 0;
                setRetagProgress('Re-tagging ' + total + ' memories…');
                // Poll every 3 seconds — check if processing memories still exist
                let attempts = 0;
                const poll = setInterval(async () => {
                  attempts++;
                  try {
                    const mems = await apiFetch('/memories?user_id=all');
                    const stillProcessing = mems.filter(m => m.processing_status === 'processing').length;
                    if (stillProcessing === 0 || attempts > 40) {
                      clearInterval(poll);
                      setRetagging(false);
                      setRetagProgress('');
                      alert('✓ Re-tagging complete! ' + total + ' memories updated.');
                    } else {
                      setRetagProgress(stillProcessing + ' memories remaining…');
                    }
                  } catch { clearInterval(poll); setRetagging(false); setRetagProgress(''); }
                }, 3000);
              } catch (e) { alert('Failed: ' + e.message); setRetagging(false); setRetagProgress(''); }
            }} disabled={retagging}>
            {retagging ? '⏳ ' + retagProgress : '🔄 Re-tag all memories'}
          </button>
        </div>
      </div>{/* end sticky */}
        {suggestions.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent, marginBottom: 10 }}>
              Suggestions — approve to add as a family fact
            </div>
            {suggestions.map((sug, i) => (
              <div key={i} style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '12px 14px', marginBottom: 8 }}>
                <div style={{ fontFamily: T.body, fontSize: 14, color: T.ink, lineHeight: 1.5, marginBottom: 8 }}>
                  {sug.description}
                  {sug.names?.length > 0 && (
                    <span style={{ color: T.accent, marginLeft: 6, fontSize: 13 }}>{sug.names.join(', ')}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={async () => {
                      try {
                        await apiFetch('/admin/facts', {
                          method: 'POST',
                          body: JSON.stringify({ raw_text: sug.description }),
                        });
                        setSuggestions(s => s.filter((_, j) => j !== i));
                        apiFetch('/admin/facts').then(f => setFacts(f)).catch(() => {});
                      } catch (e) { alert('Failed: ' + e.message); }
                    }}
                    style={{ background: T.tealSoft, border: `1px solid ${T.teal}44`, color: T.teal, fontFamily: T.body, fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 99, cursor: 'pointer' }}
                  >
                    ✓ Add as fact
                  </button>
                  <button
                    onClick={() => setSuggestions(s => s.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: `1px solid ${T.divider}`, color: T.inkMute, fontFamily: T.body, fontSize: 12, padding: '5px 12px', borderRadius: 99, cursor: 'pointer' }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {loading ? <div style={s.centerMsg}>Loading…</div> : (
        <div style={s.list}>
          {facts.map(f => (
            <div key={f.id} style={{ ...s.card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <span style={{ fontSize: 14, color: T.ink, lineHeight: 1.5 }}>{f.cleaned_text || f.raw_text}</span>
              <button onClick={() => deleteFact(f.id)} style={{ background: 'none', border: 'none', color: T.inkTert, cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}>×</button>
            </div>
          ))}
          {facts.length === 0 && <p style={s.centerMsg}>No facts yet. Add context about this person to improve AI prompts and narratives.</p>}
        </div>
      )}
    </div>
  );
}

// ─── Tree Layout ──────────────────────────────────────────────────────────────
// Custom family tree layout. Couples stay together, children drop below.

function buildLayout(members, rels) {
  if (!members.length) return null;

  const NW = 140, NH = 56, HG = 20, VG = 80, CG = 60;

  // Build lookup maps
  const spouseOf = {}; // id -> { id, status, ended_year }
  const parentsOf = {}; // child_id -> [parent_id, ...]
  const childrenOf = {}; // parent_id -> [child_id, ...]

  const allSpouses = {}; // id -> [{ id, status, ended_year }]
  rels.forEach(r => {
    if (r.type === 'spouse') {
      if (!allSpouses[r.from_id]) allSpouses[r.from_id] = [];
      allSpouses[r.from_id].push({ id: r.to_id, status: r.status, ended_year: r.ended_year });
      spouseOf[r.from_id] = { id: r.to_id, status: r.status, ended_year: r.ended_year }; // keep active/last for layout
    }
    if (r.type === 'parent') {
      if (!childrenOf[r.from_id]) childrenOf[r.from_id] = [];
      if (!childrenOf[r.from_id].includes(r.to_id)) childrenOf[r.from_id].push(r.to_id);
      if (!parentsOf[r.to_id]) parentsOf[r.to_id] = [];
      if (!parentsOf[r.to_id].includes(r.from_id)) parentsOf[r.to_id].push(r.from_id);
    }
  });

  // Build couple units: a couple is [personA, personB] or [personA] if no spouse
  // Only create one unit per couple (avoid duplicates)
  const inCouple = new Set();
  const couples = []; // each is { members: [id, id?], children: [id] }

  // Sort members so people with more relationships are processed first
  // This ensures Xenia (2 spouses) is processed before Glen (0 spouses)
  const sortedMembers = [...members].sort((a, b) => {
    const aRels = (allSpouses[a.id] || []).length + (parentsOf[a.id] || []).length;
    const bRels = (allSpouses[b.id] || []).length + (parentsOf[b.id] || []).length;
    return bRels - aRels;
  });

  sortedMembers.forEach(m => {
    if (inCouple.has(m.id)) return;
    const spouses = allSpouses[m.id] || [];
    // Prefer active spouse, then any unplaced spouse
    const activeSp = spouses.find(s => s.status === 'active' && !inCouple.has(s.id))
      || spouses.find(s => !inCouple.has(s.id));
    const unit = { members: [m.id], children: [], allSpouses: spouses };
    if (activeSp) {
      unit.members.push(activeSp.id);
      inCouple.add(activeSp.id);
    }
    inCouple.add(m.id);

    // Children = union of children of all members in this unit
    const childSet = new Set();
    unit.members.forEach(pid => (childrenOf[pid] || []).forEach(c => childSet.add(c)));
    unit.children = [...childSet];
    couples.push(unit);
  });

  // Assign generations using BFS from roots (couples with no parents)
  const coupleGen = new Map();
  const memberToCouple = new Map();
  couples.forEach((c, i) => c.members.forEach(id => memberToCouple.set(id, i)));

  // Find root couples (none of their members have parents)
  const rootCouples = couples.filter(c =>
    c.members.every(id => !parentsOf[id] || parentsOf[id].length === 0)
  );

  const queue = rootCouples.map(c => ({ couple: c, gen: 0 }));
  while (queue.length) {
    const { couple, gen } = queue.shift();
    const idx = couples.indexOf(couple);
    if (coupleGen.has(idx)) continue;
    coupleGen.set(idx, gen);
    couple.children.forEach(childId => {
      const childCoupleIdx = memberToCouple.get(childId);
      if (childCoupleIdx !== undefined && !coupleGen.has(childCoupleIdx)) {
        queue.push({ couple: couples[childCoupleIdx], gen: gen + 1 });
      }
    });
  }
  // Assign gen 0 to any unassigned
  couples.forEach((c, i) => { if (!coupleGen.has(i)) coupleGen.set(i, 0); });

  // Group couples by generation
  const byGen = {};
  couples.forEach((c, i) => {
    const g = coupleGen.get(i);
    if (!byGen[g]) byGen[g] = [];
    byGen[g].push(i);
  });

  // Assign X positions per generation
  const coupleX = new Map();
  const coupleW = (c) => c.members.length === 2 ? NW * 2 + CG : NW;

  Object.keys(byGen).sort((a, b) => a - b).forEach(g => {
    const row = byGen[g];
    const totalW = row.reduce((sum, ci) => sum + coupleW(couples[ci]), 0) + (row.length - 1) * HG * 3;
    let x = 20;
    row.forEach(ci => {
      coupleX.set(ci, x);
      x += coupleW(couples[ci]) + HG * 3;
    });
  });

  const maxGen = Math.max(...Object.keys(byGen).map(Number));
  const totalW = Math.max(...Object.keys(byGen).map(g =>
    byGen[g].reduce((sum, ci) => sum + coupleW(couples[ci]) + HG * 3, 20)
  ), 400);

  // Build final positions for each member
  const positions = {};
  couples.forEach((c, i) => {
    const x = coupleX.get(i) || 20;
    const g = coupleGen.get(i) || 0;
    const y = g * (NH + VG) + 20;
    if (c.members.length === 2) {
      positions[c.members[0]] = { x, y };
      positions[c.members[1]] = { x: x + NW + CG, y };
    } else {
      positions[c.members[0]] = { x, y };
    }
  });

  const totalH = (maxGen + 1) * (NH + VG) + 40;
  return { positions, spouseOf, childrenOf, parentsOf, couples, memberToCouple, coupleX, coupleGen, NW, NH, CG, totalW: totalW + 40, totalH };
}

function drawConnections(layout, rels) {
  if (!layout) return null;
  const { positions, spouseOf, childrenOf, couples, memberToCouple, coupleX, coupleGen, NW, NH, CG } = layout;
  const lines = [];
  const drawnSpouses = new Set();
  const drawnParents = new Set();

  // Draw spouse lines
  // Draw ALL spouse lines including ended ones
  rels.filter(r => r.type === 'spouse').forEach(r => {
    const id = r.from_id, spId = r.to_id;
    const key = [id, spId].sort().join('|');
    if (drawnSpouses.has(key)) return;
    drawnSpouses.add(key);
    const a = positions[id], b = positions[spId];
    if (!a || !b) return;
    const y = a.y + NH / 2;
    const x1 = Math.min(a.x + NW, b.x + NW);
    const x2 = Math.max(a.x, b.x);
    const isEnded = r.status === 'ended';
    if (x2 > x1) {
      lines.push(
        <line key={`sp-${key}`}
          x1={x1} y1={y} x2={x2} y2={y}
          stroke={isEnded ? '#5a5a5a' : '#4a7a5a'}
          strokeWidth={2}
          strokeDasharray={isEnded ? '8 4' : '5 3'}
          opacity={isEnded ? 0.5 : 1}
        />
      );
      if (isEnded && r.ended_year) {
        lines.push(
          <text key={`sp-yr-${key}`}
            x={(x1 + x2) / 2} y={y - 6}
            textAnchor="middle" fill="#5a5a5a" fontSize={10}
            fontFamily="system-ui,-apple-system,sans-serif">
            ended {r.ended_year}
          </text>
        );
      }
    }
  });

  // Draw parent→child lines
  // For each couple, find their shared children and draw one drop from couple midpoint
  const drawnCoupleToChild = new Set();

  couples.forEach((couple, ci) => {
    if (!couple.children.length) return;
    const key = ci + '-children';
    if (drawnCoupleToChild.has(key)) return;
    drawnCoupleToChild.add(key);

    // Find couple midpoint at bottom
    let fromX, fromY;
    if (couple.members.length === 2) {
      const a = positions[couple.members[0]];
      const b = positions[couple.members[1]];
      if (!a || !b) return;
      fromX = (a.x + NW + b.x) / 2;
      fromY = a.y + NH;
    } else {
      const a = positions[couple.members[0]];
      if (!a) return;
      fromX = a.x + NW / 2;
      fromY = a.y + NH;
    }

    // Only draw lines to children that have positions
    const validChildren = couple.children.filter(c => positions[c]);
    if (!validChildren.length) return;

    const childXs = validChildren.map(c => positions[c].x + NW / 2);
    const toY = positions[validChildren[0]].y;
    const midY = fromY + (toY - fromY) / 2;

    // Vertical line down from couple
    lines.push(<line key={`cd-${ci}`} x1={fromX} y1={fromY} x2={fromX} y2={midY} stroke="#3a5a4a" strokeWidth={1.5} />);

    if (validChildren.length === 1) {
      // Single child — straight line
      lines.push(<line key={`cc-${ci}-0`} x1={fromX} y1={midY} x2={childXs[0]} y2={midY} stroke="#3a5a4a" strokeWidth={1.5} />);
      lines.push(<line key={`cv-${ci}-0`} x1={childXs[0]} y1={midY} x2={childXs[0]} y2={toY} stroke="#3a5a4a" strokeWidth={1.5} />);
    } else {
      // Multiple children — horizontal bar then drop to each
      const barL = Math.min(...childXs);
      const barR = Math.max(...childXs);
      lines.push(<line key={`ch-${ci}`} x1={barL} y1={midY} x2={barR} y2={midY} stroke="#3a5a4a" strokeWidth={1.5} />);
      validChildren.forEach((c, i) => {
        lines.push(<line key={`cv-${ci}-${i}`} x1={childXs[i]} y1={midY} x2={childXs[i]} y2={toY} stroke="#3a5a4a" strokeWidth={1.5} />);
      });
    }
  });

  return lines;
}

// ─── Unmatched Memories Viewer ────────────────────────────────────────────────

function UnmatchedMemories({ name, onClose, onAddToTree }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    apiFetch(`/memories?search=${encodeURIComponent(name)}`)
      .then(data => { setMemories(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [name]);

  if (selected) return (
    <MemoryDetail memory={selected} onBack={() => setSelected(null)} onDelete={() => setSelected(null)} />
  );

  return (
    <div style={{ background: '#1a1200', border: '1px solid #6a4a1a', borderRadius: 14, padding: '16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#e0a060' }}>
          Memories mentioning "{name}"
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{ background: T.accent, border: 'none', color: T.ink, padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            onClick={onAddToTree}
          >
            + Add to Tree
          </button>
          <button style={{ background: 'none', border: 'none', color: T.inkTert, fontSize: 20, cursor: 'pointer', lineHeight: 1 }} onClick={onClose}>×</button>
        </div>
      </div>
      {loading ? (
        <div style={{ color: T.inkTert, fontSize: 14 }}>Loading…</div>
      ) : memories.length === 0 ? (
        <div style={{ color: T.inkTert, fontSize: 14 }}>No memories found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
          {memories.map(m => (
            <button key={m.id} style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: 10, padding: '10px 14px', textAlign: 'left', cursor: 'pointer' }} onClick={() => setSelected(m)}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 2 }}>{m.title}</div>
              <div style={{ fontSize: 12, color: T.inkTert, marginBottom: 4 }}>{m.decade} · {new Date(m.recorded_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
              {m.summary && <div style={{ fontSize: 12, color: T.inkSec, lineHeight: 1.4 }}>{m.summary}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Add Person Panel ─────────────────────────────────────────────────────────

function AddPersonPanel({ users, onSave, onCancel, prefillName }) {
  const [form, setForm] = useState({ name: prefillName || '', birth_year: '', death_year: '', user_id: '', birth_place: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [approvedSuggestions, setApprovedSuggestions] = useState([]);

  const handleUserChange = (userId) => {
    const u = users.find(x => x.id === userId);
    setForm(f => ({ ...f, user_id: userId, name: u ? u.name : f.name }));
    setSuggestions([]); setApprovedSuggestions([]);
  };

  const suggest = async () => {
    if (!form.user_id) return;
    setSuggesting(true); setSuggestions([]);
    try {
      const res = await apiFetch(`/admin/tree/suggest/${form.user_id}`);
      setSuggestions(res.suggestions || []);
      if (!res.suggestions?.length) alert(res.message || 'No suggestions found from memories.');
    } catch (e) { alert('Suggestion failed: ' + e.message); }
    setSuggesting(false);
  };

  const approveSuggestion = (sug) => {
    if (sug.field === 'birth_year') setForm(f => ({ ...f, birth_year: sug.value }));
    if (sug.field === 'birth_place') setForm(f => ({ ...f, birth_place: sug.value }));
    setApprovedSuggestions(a => [...a, sug]);
    setSuggestions(s2 => s2.filter(x => x !== sug));
  };

  const dismissSuggestion = (sug) => setSuggestions(s2 => s2.filter(x => x !== sug));

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    const notes = [form.birth_place ? `Born in ${form.birth_place}` : '', ...approvedSuggestions.filter(s2 => s2.field === 'fact').map(s2 => s2.value), form.notes].filter(Boolean).join('. ');
    await onSave({ ...form, notes });
    setSaving(false);
  };

  const confidenceColor = { high: '#80c090', medium: '#c0a050', low: '#9090a0' };

  return (
    <div style={s.treePanel}>
      <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Add Person</div>
      <div style={{ fontSize: 13, color: T.inkTert, marginBottom: 14 }}>Tip: select an existing user to auto-fill their name, then use AI to suggest details from their memories.</div>
      <div style={{ fontSize: 12, color: T.inkTert, marginBottom: 6, fontWeight: 600, letterSpacing: '0.06em' }}>LINK TO USER ACCOUNT</div>
      <select style={s.formInput} value={form.user_id} onChange={e => handleUserChange(e.target.value)}>
        <option value="">Not linked to a user account</option>
        {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
      </select>
      {form.user_id && (
        <button style={{ ...s.aggregateBtn, width: '100%', marginBottom: 12, textAlign: 'center' }} onClick={suggest} disabled={suggesting}>
          {suggesting ? '✦ Reading memories…' : '✦ Suggest details from memories'}
        </button>
      )}
      {suggesting && <WeavingLoader messages={['Reading memories…', 'Extracting facts…', 'Almost done…']} />}
      {suggestions.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: T.accent, fontWeight: 600, letterSpacing: '0.06em', marginBottom: 8 }}>AI SUGGESTIONS — tap to approve</div>
          {suggestions.map((sug, i) => (
            <div key={i} style={s.suggestionCard}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: confidenceColor[sug.confidence] || '#888', textTransform: 'uppercase' }}>{sug.confidence}</span>
                  <span style={{ fontSize: 12, color: T.inkTert, fontWeight: 600 }}>{sug.field === 'birth_year' ? 'Birth Year' : sug.field === 'birth_place' ? 'Birth Place' : 'Fact'}</span>
                </div>
                <div style={{ fontSize: 14, color: T.ink, marginBottom: 4 }}>{sug.value}</div>
                {sug.evidence && <div style={{ fontSize: 12, color: T.inkTert, fontStyle: 'italic' }}>"{sug.evidence}"</div>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <button style={s.approveBtn} onClick={() => approveSuggestion(sug)}>✓ Use</button>
                <button style={s.dismissBtn} onClick={() => dismissSuggestion(sug)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {approvedSuggestions.length > 0 && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: '#1a3a2a', borderRadius: 10, fontSize: 13, color: '#80c090' }}>
          ✓ {approvedSuggestions.length} suggestion{approvedSuggestions.length > 1 ? 's' : ''} approved
        </div>
      )}
      <div style={{ fontSize: 12, color: T.inkTert, marginBottom: 6, fontWeight: 600, letterSpacing: '0.06em', marginTop: 4 }}>DETAILS</div>
      <input style={s.formInput} placeholder="Full name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      <input style={s.formInput} placeholder="Birth year (e.g. 1948)" type="number" value={form.birth_year} onChange={e => setForm(f => ({ ...f, birth_year: e.target.value }))} />
      <input style={s.formInput} placeholder="Birth place (e.g. San Juan, Texas)" value={form.birth_place} onChange={e => setForm(f => ({ ...f, birth_place: e.target.value }))} />
      <input style={s.formInput} placeholder="Death year (leave blank if living)" type="number" value={form.death_year} onChange={e => setForm(f => ({ ...f, death_year: e.target.value }))} />
      <textarea style={{ ...s.formInput, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Notes or bio (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={s.saveBtn} onClick={handleSave} disabled={saving || !form.name?.trim()}>{saving ? 'Saving…' : 'Add Person'}</button>
        <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Tree Tab ─────────────────────────────────────────────────────────────────

function TreeTab() {
  const [members, setMembers] = useState([]);
  const [rels, setRels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [panel, setPanel] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [memories, setMemories] = useState([]);
  const [loadingMemories, setLoadingMemories] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [unmatched, setUnmatched] = useState([]);
  const [showUnmatched, setShowUnmatched] = useState(true);
  const [unmatchedSearch, setUnmatchedSearch] = useState(null);
  const [unmatchedAddName, setUnmatchedAddName] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, r, u, um] = await Promise.all([
        apiFetch('/tree/members'),
        apiFetch('/tree/relationships'),
        apiFetch('/admin/users'),
        apiFetch('/tree/unmatched-people'),
      ]);
      setMembers(m); setRels(r); setUsers(u);
      setUnmatched(um.unmatched || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const layout = useMemo(() => buildLayout(members, rels), [members, rels]);
  const closePanel = () => { setPanel(null); setSelected(null); setForm({}); };

  const selectMember = async (id) => {
    setSelected(id);
    if (!editMode) {
      const m = members.find(x => x.id === id);
      setPanel('view');
      if (m?.user_id) {
        setLoadingMemories(true);
        setMemories(await apiFetch(`/memories?user_id=${m.user_id}`).catch(() => []));
        setLoadingMemories(false);
      } else { setMemories([]); }
    } else { setPanel('editActions'); }
  };

  const addPerson = async (formData) => {
    try { await apiFetch('/tree/members', { method: 'POST', body: JSON.stringify(formData) }); setShowAddPanel(false); load(); }
    catch (e) { alert(e.message); }
  };

  const updatePerson = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try { await apiFetch(`/tree/members/${selected}`, { method: 'PUT', body: JSON.stringify(form) }); closePanel(); load(); }
    catch (e) { alert(e.message); }
    setSaving(false);
  };

  const deletePerson = async () => {
    if (!window.confirm('Remove this person from the tree?')) return;
    await apiFetch(`/tree/members/${selected}`, { method: 'DELETE' });
    closePanel(); load();
  };

  const addRel = async () => {
    if (!form.to_id) return;
    setSaving(true);
    try {
      await apiFetch('/tree/relationships', { method: 'POST', body: JSON.stringify({ from_id: selected, to_id: form.to_id, type: form.relType }) });
      setPanel('editActions'); setForm(f => ({ ...f, to_id: '' })); load();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const openEditForm = () => {
    const m = members.find(x => x.id === selected);
    setForm({ name: m.name, birth_year: m.birth_year || '', death_year: m.death_year || '', user_id: m.user_id || '' });
    setPanel('editForm');
  };

  const selectedMember = members.find(m => m.id === selected);
  if (loading) return <div style={s.centerMsg}>Loading tree…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 17, fontWeight: 600, color: T.ink }}>Family Tree</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {editMode && <button style={s.addBtn} onClick={() => { setShowAddPanel(true); closePanel(); }}>+ Add Person</button>}
          <button style={{ ...s.aggregateBtn, ...(editMode ? { background: T.accent, color: T.ink } : {}) }} onClick={() => { setEditMode(!editMode); closePanel(); setShowAddPanel(false); }}>
            {editMode ? '✓ Done' : 'Edit Tree'}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, fontSize: 12, color: T.inkTert, flexWrap: 'wrap' }}>
        <span>── spouse</span>
        <span style={{ color: '#80c090' }}>● linked storyteller</span>
        <span>tap a person to explore</span>
        {unmatched.length > 0 && (
          <button onClick={() => setShowUnmatched(!showUnmatched)} style={{ background: '#3a2a1a', border: '1px solid #8a5a2a', color: '#e0a060', borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' }}>
            ⚠ {unmatched.length} unrecognized {unmatched.length === 1 ? 'person' : 'people'}
          </button>
        )}
      </div>

      {unmatchedSearch && (
              <UnmatchedMemories
                name={unmatchedSearch}
                onClose={() => setUnmatchedSearch(null)}
                onAddToTree={() => { setShowAddPanel(true); setUnmatchedAddName(unmatchedSearch); setUnmatchedSearch(null); closePanel(); }}
              />
            )}

      {showUnmatched && unmatched.length > 0 && (
        <div style={{ background: '#1a1200', border: '1px solid #6a4a1a', borderRadius: 14, padding: '16px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#e0a060', marginBottom: 8 }}>⚠ People mentioned in memories but not in the family tree</div>
          <div style={{ fontSize: 12, color: T.inkTert, marginBottom: 12 }}>These names appear in 2 or more recordings. Tap to add them to the tree.</div>
          {unmatched.map((person, i) => (
            <div key={i} style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div>
                  <button
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                    onClick={() => { setUnmatchedSearch(person.name); setShowUnmatched(false); }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 600, color: T.accent, textDecoration: 'underline' }}>{person.name}</span>
                  </button>
                  <span style={{ fontSize: 12, color: T.inkTert, marginLeft: 8 }}>in {person.count} memories — click to view</span>
                </div>
                <button
                  style={{ background: T.accent, border: 'none', color: T.ink, padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => {
                    setShowAddPanel(true);
                    setShowUnmatched(false);
                    closePanel();
                    setUnmatchedAddName(person.name);
                  }}
                >
                  + Add to Tree
                </button>
              </div>
              {person.suggestions.length > 0
                ? <div style={{ fontSize: 12, color: T.inkTert }}>Possible match: {person.suggestions.map((s, j) => <span key={j} style={{ color: T.accent, marginRight: 8 }}>{s.name}</span>)}</div>
                : <div style={{ fontSize: 12, color: T.inkTert }}>No similar names in tree</div>
              }
            </div>
          ))}
        </div>
      )}
      {members.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🌳</div>
          <div style={{ fontSize: 17, color: T.inkSec, marginBottom: 8 }}>No family members yet</div>
          <div style={{ fontSize: 14, color: T.inkTert, marginBottom: 24 }}>Start by adding the oldest generation first.</div>
          <button style={s.addBtn} onClick={() => { setEditMode(true); setShowAddPanel(true); }}>+ Add First Person</button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: `1px solid ${T.divider}`, borderRadius: 14, background: '#111' }}>
          <svg width={layout.totalW} height={layout.totalH} style={{ display: 'block' }}>
            {drawConnections(layout, rels)}
            {members.map(m => {
              const pos = layout.positions[m.id]; if (!pos) return null;
              const isSel = selected === m.id, isLinked = !!m.user_id;
              const label = m.name.length > 15 ? m.name.slice(0, 14) + '…' : m.name;
              const yearLabel = m.birth_year ? (m.death_year ? `${m.birth_year}–${m.death_year}` : `b. ${m.birth_year}`) : '';
              return (
                <g key={m.id} onClick={() => selectMember(m.id)} style={{ cursor: 'pointer' }}>
                  <rect x={pos.x} y={pos.y} width={layout.NW} height={layout.NH} rx={10} ry={10} fill={isSel ? '#1c3a28' : '#1a1a1a'} stroke={isSel ? T.accent : isLinked ? '#3a6a4a' : '#2e2e2e'} strokeWidth={isSel ? 2 : 1} />
                  <text x={pos.x + layout.NW / 2} y={pos.y + (yearLabel ? layout.NH / 2 - 3 : layout.NH / 2 + 6)} textAnchor="middle" fill={isLinked ? '#86c99a' : '#d0d0d0'} fontSize={13} fontWeight={600} fontFamily="system-ui,-apple-system,sans-serif">{label}</text>
                  {yearLabel && <text x={pos.x + layout.NW / 2} y={pos.y + layout.NH / 2 + 14} textAnchor="middle" fill="#555" fontSize={11} fontFamily="system-ui,-apple-system,sans-serif">{yearLabel}</text>}
                  {isLinked && <circle cx={pos.x + layout.NW - 11} cy={pos.y + 11} r={4} fill={T.accent} />}
                </g>
              );
            })}
          </svg>
        </div>
      )}
      {showAddPanel && <AddPersonPanel users={users} onSave={addPerson} onCancel={() => { setShowAddPanel(false); setUnmatchedAddName(null); }} prefillName={unmatchedAddName} />}
      {panel === 'view' && selectedMember && (
        <div style={s.treePanel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>{selectedMember.name}</div>
              {selectedMember.birth_year && <div style={{ fontSize: 13, color: T.inkTert, marginTop: 2 }}>{selectedMember.birth_year}{selectedMember.death_year ? ` – ${selectedMember.death_year}` : ''}</div>}
              {selectedMember.user_name && <div style={{ fontSize: 13, color: T.accent, marginTop: 4 }}>🎙 {selectedMember.user_name}</div>}
            </div>
            <button style={s.closeBtn} onClick={closePanel}>×</button>
          </div>
          {selectedMember.user_id ? (
            loadingMemories ? <div style={{ color: T.inkTert, fontSize: 14 }}>Loading stories…</div> :
            memories.length === 0 ? <div style={{ color: T.inkTert, fontSize: 14 }}>No recordings yet.</div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
                {memories.map(m => (
                  <div key={m.id} style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 2 }}>{m.title}</div>
                    <div style={{ fontSize: 12, color: T.inkTert }}>{m.decade} · {new Date(m.recorded_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                    {m.summary && <div style={{ fontSize: 12, color: T.inkSec, marginTop: 4, lineHeight: 1.4 }}>{m.summary}</div>}
                  </div>
                ))}
              </div>
            )
          ) : <div style={{ color: T.inkTert, fontSize: 14 }}>Not linked to a storyteller account.</div>}
        </div>
      )}
      {panel === 'editActions' && selectedMember && (
        <div style={s.treePanel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 17, fontWeight: 600, color: T.ink }}>{selectedMember.name}</div>
            <button style={s.closeBtn} onClick={closePanel}>×</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <button style={s.actionBtn} onClick={openEditForm}>✏️  Edit Details</button>
            <button style={s.actionBtn} onClick={() => { setForm({ relType: 'spouse' }); setPanel('addRel'); }}>💍  Add Spouse</button>
            <button style={s.actionBtn} onClick={() => setPanel('endRel')}>💔  Mark Marriage as Ended</button>
            <button style={s.actionBtn} onClick={() => { setForm({ relType: 'parent' }); setPanel('addRel'); }}>👶  Add as Parent of…</button>
            <button style={{ ...s.actionBtn, background: '#2a1212', color: '#d07070', borderColor: '#5a2a2a' }} onClick={deletePerson}>Remove from Tree</button>
          </div>
        </div>
      )}
      {panel === 'editForm' && (
        <div style={s.treePanel}>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 14 }}>Edit Person</div>
          <input style={s.formInput} placeholder="Full name *" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input style={s.formInput} placeholder="Birth year" type="number" value={form.birth_year || ''} onChange={e => setForm(f => ({ ...f, birth_year: e.target.value }))} />
          <input style={s.formInput} placeholder="Death year (leave blank if living)" type="number" value={form.death_year || ''} onChange={e => setForm(f => ({ ...f, death_year: e.target.value }))} />
          <select style={s.formInput} value={form.user_id || ''} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}>
            <option value="">Not linked to a user account</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.saveBtn} onClick={updatePerson} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button style={s.cancelBtn} onClick={() => setPanel('editActions')}>Cancel</button>
          </div>
        </div>
      )}
      {panel === 'endRel' && selectedMember && (
        <div style={s.treePanel}>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Mark Marriage as Ended</div>
          <div style={{ fontSize: 13, color: T.inkTert, marginBottom: 14 }}>
            This will show the marriage with a grey dashed line on the tree. Children remain linked to both parents.
          </div>
          <select style={s.formInput} value={form.endRelId || ''} onChange={e => setForm(f => ({ ...f, endRelId: e.target.value }))}>
            <option value="">Select spouse…</option>
            {rels.filter(r => r.from_id === selected && r.type === 'spouse').map(r => {
              const spouse = members.find(m => m.id === r.to_id);
              return <option key={r.id} value={r.id}>{spouse?.name || 'Unknown'} {r.status === 'ended' ? '(already ended)' : ''}</option>;
            })}
          </select>
          <input
            style={s.formInput}
            placeholder="Year ended (e.g. 1995, optional)"
            type="number"
            value={form.endedYear || ''}
            onChange={e => setForm(f => ({ ...f, endedYear: e.target.value }))}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={s.saveBtn}
              disabled={saving || !form.endRelId}
              onClick={async () => {
                setSaving(true);
                try {
                  await apiFetch(`/tree/relationships/${form.endRelId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ status: 'ended', ended_year: form.endedYear ? parseInt(form.endedYear) : null })
                  });
                  closePanel(); load();
                } catch (e) { alert(e.message); }
                setSaving(false);
              }}
            >
              {saving ? 'Saving…' : 'Mark as Ended'}
            </button>
            <button style={s.cancelBtn} onClick={() => setPanel('editActions')}>Cancel</button>
          </div>
        </div>
      )}
      {panel === 'addRel' && selectedMember && (
        <div style={s.treePanel}>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
            {form.relType === 'spouse' ? `Add Spouse for ${selectedMember.name}` : `${selectedMember.name} is parent of…`}
          </div>
          <div style={{ fontSize: 13, color: T.inkTert, marginBottom: 14 }}>
            {form.relType === 'spouse' ? 'Select the person they are married to.' : 'Select the child to link.'}
          </div>
          <select style={s.formInput} value={form.to_id || ''} onChange={e => setForm(f => ({ ...f, to_id: e.target.value }))}>
            <option value="">Select person…</option>
            {members.filter(m => m.id !== selected).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.saveBtn} onClick={addRel} disabled={saving || !form.to_id}>{saving ? 'Saving…' : 'Link'}</button>
            <button style={s.cancelBtn} onClick={() => setPanel('editActions')}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab({ onImpersonate }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'storyteller', family_group_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ family_group_id: '', role: 'storyteller' });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [u, g] = await Promise.all([apiFetch('/admin/users'), apiFetch('/admin/groups')]);
      setUsers(u); setGroups(g);
      setForm(f => ({ ...f, family_group_id: g.length > 0 ? g[0].id : '' }));
      setInviteForm(f => ({ ...f, family_group_id: g.length > 0 ? g[0].id : '' }));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);
  const removeMembership = async (userId, groupId) => {
    if (!window.confirm('Remove this person from this family group?')) return;
    try {
      await apiFetch(`/admin/users/${userId}/memberships/${groupId}`, { method: 'DELETE' });
      load();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      await apiFetch('/admin/groups', { method: 'POST', body: JSON.stringify({ name: newGroupName.trim() }) });
      setNewGroupName('');
      load();
    } catch (e) { alert('Failed: ' + e.message); }
    setCreatingGroup(false);
  };

  const generateInvite = async () => {
    setInviteLoading(true); setInviteResult(null);
    try {
      const res = await apiFetch('/admin/invite', { method: 'POST', body: JSON.stringify(inviteForm) });
      setInviteResult(res);
    } catch (e) { alert('Failed to generate invite: ' + e.message); }
    setInviteLoading(false);
  };

  const copyLink = () => {
    if (!inviteResult) return;
    navigator.clipboard.writeText(inviteResult.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const save = async () => {
    if (!form.name || !form.email) return setError('Name and email are required');
    setSaving(true); setError('');
    try {
      await apiFetch('/admin/users', { method: 'POST', body: JSON.stringify(form) });
      setShowForm(false); setForm(f => ({ name: '', email: '', role: 'storyteller', family_group_id: f.family_group_id })); load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Remove this user? Their recordings will remain but they will no longer be able to log in.')) return;
    await apiFetch('/admin/users/' + id, { method: 'DELETE' }); load();
  };

  const startEdit = (u) => { setEditingId(u.id); setEditForm({ name: u.name, email: u.email, role: u.role, family_group_id: u.family_group_id || '', nickname: u.nickname || '' }); setEditError(''); };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); setEditError(''); };

  const saveEdit = async (id) => {
    if (!editForm.name || !editForm.email) return setEditError('Name and email are required');
    setEditSaving(true); setEditError('');
    try { await apiFetch('/admin/users/' + id, { method: 'PUT', body: JSON.stringify(editForm) }); setEditingId(null); load(); }
    catch (e) { setEditError(e.message); }
    setEditSaving(false);
  };

  return (
    <>
      <div style={s.formBox}>
        <div style={{ fontSize: 13, color: T.accent, fontWeight: 600, letterSpacing: '0.08em', marginBottom: 12 }}>FAMILY GROUPS</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={{ ...s.formInput, marginBottom: 0, flex: 1 }}
            placeholder="New family group name..."
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createGroup()}
          />
          <button style={s.addBtn} onClick={createGroup} disabled={creatingGroup || !newGroupName.trim()}>
            {creatingGroup ? '...' : 'Create'}
          </button>
        </div>
        {groups.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {groups.map(g => (
              <FamilyGroupPill key={g.id} group={g} onRenamed={load} />
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 17, fontWeight: 600, color: T.ink }}>Users ({users.length})</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...s.addBtn, background: T.accentDim, color: T.accent }} onClick={() => { setShowInvite(!showInvite); setInviteResult(null); }}>
            {showInvite ? 'Cancel' : 'Invite Link'}
          </button>
          <button style={s.addBtn} onClick={() => { setShowForm(!showForm); setError(''); }}>{showForm ? 'Cancel' : '+ Add User'}</button>
        </div>
      </div>
      {showInvite && (
        <div style={s.formBox}>
          <div style={{ fontSize: 13, color: T.accent, fontWeight: 600, letterSpacing: '0.08em', marginBottom: 12 }}>GENERATE INVITE LINK</div>
          <div style={{ fontSize: 13, color: T.inkSec, marginBottom: 14, lineHeight: 1.5 }}>
            Send this link to someone so they can join and set up their own passkey. Links expire after 7 days.
          </div>
          <select style={s.formInput} value={inviteForm.family_group_id} onChange={e => setInviteForm(f => ({ ...f, family_group_id: e.target.value }))}>
            <option value="">Select family group...</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select style={s.formInput} value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
            <option value="storyteller">Storyteller - can record memories</option>
            <option value="viewer">Viewer - can browse and listen</option>
            <option value="family_admin">Family Admin - can manage their group</option>
          </select>
          {inviteResult === null ? (
            <button style={s.saveBtn} onClick={generateInvite} disabled={inviteLoading || !inviteForm.family_group_id}>
              {inviteLoading ? 'Generating...' : 'Generate Invite Link'}
            </button>
          ) : (
            <div style={{ marginTop: 4 }}>
              <div style={{ background: T.paperHigh, border: '1px solid ' + T.divider, borderRadius: 10, padding: '12px 14px', marginBottom: 10, wordBreak: 'break-all', fontSize: 13, color: T.accent, lineHeight: 1.5 }}>
                {inviteResult.url}
              </div>
              <div style={{ fontSize: 12, color: T.inkTert, marginBottom: 10 }}>
                Expires: {new Date(inviteResult.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - Role: {inviteResult.role}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.saveBtn} onClick={copyLink}>{copied ? 'Copied!' : 'Copy Link'}</button>
                <button style={s.cancelBtn} onClick={() => setInviteResult(null)}>New Link</button>
              </div>
            </div>
          )}
        </div>
      )}
      {showForm && (
        <div style={s.formBox}>
          <input className="adm-input" style={s.formInput} placeholder="Full name (e.g. Maria Garcia)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input className="adm-input" style={s.formInput} placeholder="Email address" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <select style={s.formInput} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="storyteller">Storyteller</option>
            <option value="viewer">Viewer</option>
            <option value="family_admin">Family Admin</option>
            <option value="admin">Platform Admin</option>
          </select>
          <select style={s.formInput} value={form.family_group_id} onChange={e => setForm(f => ({ ...f, family_group_id: e.target.value }))}>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {error && <div style={s.errorMsg}>{error}</div>}
          <button style={s.saveBtn} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Add User'}</button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {users.map(u => (
          <div key={u.id} style={s.userCard}>
            {editingId === u.id ? (
              <div style={{ padding: '16px 18px' }}>
                <div style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.accent, marginBottom: 12 }}>Editing name, nickname &amp; email</div>
                <input style={s.formInput} placeholder="Full name" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                <input style={s.formInput} placeholder="Nickname — what we call them (e.g. Mario, Mom)" value={editForm.nickname || ''} onChange={e => setEditForm(f => ({ ...f, nickname: e.target.value }))} />
                <input style={s.formInput} placeholder="Email address" type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                {editError && <div style={s.errorMsg}>{editError}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button style={s.saveBtn} onClick={() => saveEdit(u.id)} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save'}</button>
                  <button style={s.cancelBtn} onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: T.display, fontSize: 18, color: T.ink, letterSpacing: -0.2, marginBottom: 2 }}>{u.name}{u.nickname ? <span style={{ fontFamily: T.body, fontSize: 13, color: T.accent, marginLeft: 8 }}>({u.nickname})</span> : null}</div>
                    <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>{u.email}</div>
                    {u.passkey_count === 0 && <div style={{ fontFamily: T.body, fontSize: 12, color: T.accent, marginTop: 4 }}>⚠ No passkey set up yet</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button style={s.editUserBtn} onClick={() => startEdit(u)}>Edit</button>
                    <button onClick={() => onImpersonate && onImpersonate(u)} style={{ background: T.tealSoft, border: `1px solid ${T.teal}44`, color: T.teal, padding: '7px 14px', borderRadius: T.radius, fontFamily: T.body, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>View as</button>
                    <button style={s.deleteUserBtn} onClick={() => deleteUser(u.id)}>Remove</button>
                  </div>
                </div>
                <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 10 }}>
                  <div style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 8 }}>Family access</div>
                  {u.memberships && u.memberships.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                      {u.memberships.map(m => (
                        <div key={m.family_group_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.paper, border: `1px solid ${T.divider}`, borderRadius: 10, padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontFamily: T.body, fontSize: 14, color: T.ink, fontWeight: 500 }}>{m.group_name}</span>
                            <MembershipRoleSelector userId={u.id} groupId={m.family_group_id} currentRole={m.role} onChanged={load} />
                          </div>
                          <button onClick={() => removeMembership(u.id, m.family_group_id)} style={{ background: 'none', border: 'none', color: T.inkMute, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, marginBottom: 8 }}>Not in any family group yet.</div>
                  )}
                  <AddMembershipRow userId={u.id} groups={groups} onAdded={load} />
                  <SuggestedPromptsPanel userId={u.id} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Suggested Prompts Panel ──────────────────────────────────────────────────

function SuggestedPromptsPanel({ userId }) {
  const [open, setOpen] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState(null);

  const openMemory = async (memoryId) => {
    if (!memoryId) return;
    try {
      const data = await apiFetch(`/memories?user_id=${userId}`);
      const m = data.find(x => x.id === memoryId);
      if (m) setSelectedMemory(m);
    } catch (e) { alert('Could not load memory: ' + e.message); }
  };


  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/admin/suggested-prompts/${userId}`);
      setPrompts(data);
    } catch {}
    setLoading(false);
  };

  const toggle = () => {
    if (!open) load();
    setOpen(o => !o);
  };

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const p = await apiFetch('/admin/suggested-prompts', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, text: text.trim() }),
      });
      setPrompts(prev => [...prev, p]);
      setText('');
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await apiFetch(`/admin/suggested-prompts/${id}`, { method: 'DELETE' });
      setPrompts(prev => prev.filter(p => p.id !== id));
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const requeue = async (p, followUp) => {
    setSaving(true);
    try {
      const newText = followUp
        ? await apiFetch('/admin/suggested-prompts/follow-up', {
            method: 'POST',
            body: JSON.stringify({ original_text: p.text, user_id: userId }),
          }).then(r => r.text)
        : p.text;
      const created = await apiFetch('/admin/suggested-prompts', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, text: newText }),
      });
      setPrompts(prev => [...prev, created]);
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };

  const unused = prompts.filter(p => !p.used);
  const used = prompts.filter(p => p.used);

  if (selectedMemory) return (
    <MemoryDetail
      memory={selectedMemory}
      onBack={() => setSelectedMemory(null)}
      onDelete={() => setSelectedMemory(null)}
      onUpdate={(updated) => setSelectedMemory(updated)}
    />
  );

  return (
    <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 10, marginTop: 6 }}>
      <button onClick={toggle} style={{ background: 'none', border: 'none', color: T.accent, fontFamily: T.body, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 15 }}>✦</span>
        {open ? 'Hide prompt queue' : `Suggest prompts${unused.length > 0 ? ` (${unused.length} queued)` : ''}`}
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {/* Add new prompt */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder="Type a prompt for this storyteller…"
              style={{ flex: 1, background: T.paperHigh, border: `1.5px solid ${T.divider}`, color: T.ink, padding: '10px 14px', borderRadius: T.radius, fontFamily: T.body, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
            <button onClick={add} disabled={saving || !text.trim()} style={{ background: T.ink, border: 'none', color: T.paper, padding: '10px 16px', borderRadius: T.radius, fontFamily: T.body, fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {saving ? '…' : 'Add'}
            </button>
          </div>

          {loading && <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>Loading…</div>}

          {/* Queued prompts */}
          {unused.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.accent, marginBottom: 6 }}>Queued — next up</div>
              {unused.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '10px 12px', marginBottom: 6 }}>
                  <span style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute, paddingTop: 1, flexShrink: 0 }}>{i + 1}.</span>
                  <span style={{ fontFamily: T.body, fontSize: 14, color: T.ink, lineHeight: 1.5, flex: 1 }}>{p.text}</span>
                  <button onClick={() => remove(p.id)} style={{ background: 'none', border: 'none', color: T.inkMute, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* Used prompts */}
          {used.length > 0 && (
            <div>
              <div style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 6 }}>Already used</div>
              {used.map(p => {
                const status = p.prompt_status;
                const stillProcessing = p.memory_id && p.memory_processing_status !== 'done';
                const cfg = status === 'answered'
                  ? { label: '✓ Answered', color: T.teal, bg: T.tealSoft }
                  : status === 'weak'
                  ? { label: '~ Weak answer', color: T.accent, bg: T.accentSoft }
                  : status === 'off_topic'
                  ? { label: '— Off topic', color: T.inkMute, bg: T.divider }
                  : stillProcessing
                  ? { label: '⏳ Processing…', color: T.inkMute, bg: T.divider }
                  : null;
                return (
                  <div key={p.id} style={{ background: 'transparent', border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '10px 12px', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, lineHeight: 1.5, textDecoration: status === 'answered' ? 'line-through' : 'none', opacity: status === 'answered' ? 0.6 : 1 }}>{p.text}</span>
                        {p.memory_id && <button onClick={() => openMemory(p.memory_id)} style={{ display: 'inline-block', marginLeft: 8, background: T.paperHigh, border: `1px solid ${T.divider}`, color: T.accent, fontFamily: T.body, fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 99, cursor: 'pointer' }}>→ view memory</button>}
                      </div>
                    </div>
                    {cfg && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, borderRadius: 99, padding: '2px 10px' }}>{cfg.label}</span>
                        {(status === 'weak' || status === 'off_topic') && (
                          <button onClick={() => requeue(p, true)} disabled={saving} style={{ background: 'none', border: `1px solid ${T.divider}`, color: T.accent, fontFamily: T.body, fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 99, cursor: 'pointer' }}>
                            ↻ Re-queue follow-up
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && prompts.length === 0 && (
            <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, fontStyle: 'italic' }}>No prompts yet. Add one above and it will appear at the top of their queue next time they tap "Give me a question."</div>
          )}
        </div>
      )}
    </div>
  );
}

function FamilyGroupPill({ group, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || name.trim() === group.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await apiFetch('/admin/groups/' + group.id, { method: 'PUT', body: JSON.stringify({ name: name.trim() }) });
      onRenamed();
      setEditing(false);
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };

  if (editing) return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        autoFocus
        style={{ background: T.paper, border: `1.5px solid ${T.accent}`, color: T.ink, padding: '4px 10px', borderRadius: 20, fontFamily: T.body, fontSize: 13, outline: 'none', width: 160 }}
      />
      <button onClick={save} disabled={saving} style={{ background: T.ink, border: 'none', color: T.paper, padding: '4px 10px', borderRadius: 99, fontFamily: T.body, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        {saving ? '…' : 'Save'}
      </button>
      <button onClick={() => { setName(group.name); setEditing(false); }} style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
    </div>
  );

  return (
    <button onClick={() => setEditing(true)} title="Click to rename" style={{
      background: 'rgba(78,138,144,0.12)', border: '1px solid rgba(78,138,144,0.25)',
      color: '#3a7a80', padding: '4px 12px', borderRadius: 20,
      fontFamily: T.body, fontSize: 13, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {group.name}
      <span style={{ fontSize: 10, opacity: 0.6 }}>✎</span>
    </button>
  );
}

function MembershipRoleSelector({ userId, groupId, currentRole, onChanged }) {
  const [saving, setSaving] = useState(false);
  const handleChange = async (newRole) => {
    setSaving(true);
    try {
      await apiFetch('/admin/users/' + userId + '/memberships', {
        method: 'POST',
        body: JSON.stringify({ family_group_id: groupId, role: newRole }),
      });
      onChanged();
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };
  const colors = {
    family_admin: { bg: 'rgba(78,138,144,0.15)', color: '#3a7a80' },
    storyteller:  { bg: 'rgba(199,90,44,0.12)',  color: '#a04820' },
    viewer:       { bg: 'rgba(34,24,14,0.07)',   color: '#6a5a3e' },
  };
  const col = colors[currentRole] || colors.viewer;
  return (
    <select value={currentRole} onChange={e => handleChange(e.target.value)} disabled={saving}
      style={{ background: col.bg, color: col.color, border: 'none', borderRadius: 99, fontFamily: T.body, fontSize: 12, fontWeight: 600, padding: '3px 10px', cursor: 'pointer', outline: 'none' }}>
      <option value="storyteller">Storyteller — can record memories</option>
      <option value="viewer">Viewer — can browse and listen</option>
      <option value="family_admin">Family Admin — can record + manage group</option>
    </select>
  );
}

function AddMembershipRow({ userId, groups, onAdded }) {
  const [show, setShow] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [role, setRole] = useState('storyteller');
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!groupId) return;
    setSaving(true);
    try {
      await apiFetch('/admin/users/' + userId + '/memberships', {
        method: 'POST',
        body: JSON.stringify({ family_group_id: groupId, role }),
      });
      setShow(false); setGroupId(''); setRole('storyteller');
      onAdded();
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };
  if (!show) return (
    <button onClick={() => setShow(true)} style={{ background: 'none', border: '1px dashed rgba(34,24,14,0.20)', color: T.inkMute, fontFamily: T.body, fontSize: 13, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', marginTop: 4 }}>
      + Add to another family group
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
      <select value={groupId} onChange={e => setGroupId(e.target.value)}
        style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, color: T.ink, padding: '6px 10px', borderRadius: 8, fontFamily: T.body, fontSize: 13, outline: 'none' }}>
        <option value="">Select group…</option>
        {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
      </select>
      <select value={role} onChange={e => setRole(e.target.value)}
        style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, color: T.ink, padding: '6px 10px', borderRadius: 8, fontFamily: T.body, fontSize: 13, outline: 'none' }}>
        <option value="storyteller">Storyteller</option>
        <option value="viewer">Viewer</option>
        <option value="family_admin">Family Admin</option>
      </select>
      <button onClick={add} disabled={saving || !groupId}
        style={{ background: T.ink, border: 'none', color: T.paper, padding: '6px 12px', borderRadius: 8, fontFamily: T.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        {saving ? '…' : 'Add'}
      </button>
      <button onClick={() => setShow(false)}
        style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, cursor: 'pointer' }}>
        Cancel
      </button>
    </div>
  );
}

function roleBg(role) {
  if (role === 'admin')        return { bg: 'rgba(34,24,14,0.12)', color: '#22180e' };
  if (role === 'family_admin') return { bg: 'rgba(78,138,144,0.15)', color: '#3a7a80' };
  if (role === 'storyteller')  return { bg: 'rgba(199,90,44,0.12)', color: '#a04820' };
  return { bg: 'rgba(34,24,14,0.07)', color: '#6a5a3e' };
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  // Layout
  wrap:       { minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' },
  content:    { padding: '20px', maxWidth: 860, margin: '0 auto', width: '100%' },

  // Filters
  filters:    { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 },
  searchInput:{ background: T.paperHigh, border: `1.5px solid ${T.divider}`, color: T.ink, padding: '12px 16px', borderRadius: T.radiusLg, fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: T.body },
  filterRow:  { display: 'flex', gap: 8 },
  select:     { flex: 1, background: T.paperHigh, border: `1.5px solid ${T.divider}`, color: T.ink, padding: '10px 12px', borderRadius: T.radius, fontSize: 14, outline: 'none', cursor: 'pointer', fontFamily: T.body },
  aggregateBtn:{ background: T.ink, border: 'none', color: T.paper, padding: '10px 16px', borderRadius: T.radiusLg, fontSize: 14, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: T.body },

  // Memory cards
  count:      { fontFamily: T.body, fontSize: 13, color: T.inkMute, marginBottom: 10 },
  list:       { display: 'flex', flexDirection: 'column', gap: 8 },
  card:       { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', textAlign: 'left', width: '100%', cursor: 'pointer', boxShadow: '0 1px 0 rgba(34,24,14,0.02)', fontFamily: T.body },
  cardMeta:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
  cardDecade: { fontFamily: T.body, fontSize: 11, color: T.accent, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' },
  cardDate:   { fontFamily: T.body, fontSize: 12, color: T.inkMute },
  cardTitle:  { fontFamily: T.display, fontSize: 18, color: T.ink, marginBottom: 4, letterSpacing: -0.2, lineHeight: 1.2 },
  cardLoc:    { fontFamily: T.body, fontSize: 13, color: T.inkMute, marginBottom: 4 },
  cardSummary:{ fontFamily: T.body, fontSize: 14, color: T.inkSoft, lineHeight: 1.55 },
  cardTags:   { display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 },
  tag:        { fontFamily: T.body, fontSize: 11, padding: '3px 10px', borderRadius: 99, background: 'rgba(34,24,14,0.07)', color: 'rgba(34,24,14,0.65)', fontWeight: 500 },

  // Aggregation / weave
  aggregationBox:  { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '20px 18px', marginBottom: 16 },
  aggregationLabel:{ fontFamily: T.body, fontSize: 11, color: T.accent, fontWeight: 700, letterSpacing: '0.12em', marginBottom: 12, textTransform: 'uppercase' },
  aggregationText: { fontFamily: T.body, fontSize: 15, color: T.ink, lineHeight: 1.8, margin: '0 0 16px' },
  gapsBox:    { borderTop: `1px solid ${T.divider}`, paddingTop: 14, marginTop: 4 },
  gapsLabel:  { fontFamily: T.body, fontSize: 11, color: T.inkMute, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 10, textTransform: 'uppercase' },
  gapItem:    { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 },
  gapNum:     { fontFamily: T.body, fontSize: 12, color: T.accent, fontWeight: 700, minWidth: 18, paddingTop: 1 },
  gapText:    { fontFamily: T.body, fontSize: 14, color: T.inkSoft, lineHeight: 1.5 },
  clearAgg:   { background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, cursor: 'pointer', marginTop: 12, padding: 0, display: 'block' },
  weavingBox: { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '28px 20px', marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
  weavingSpinner:{ display: 'flex', gap: 6, alignItems: 'center' },
  weavingDot: { width: 8, height: 8, borderRadius: '50%', background: T.accent, animation: 'weaveBounce 1.2s ease-in-out infinite' },
  weavingMsg: { fontFamily: T.body, fontSize: 14, color: T.inkSoft, fontStyle: 'italic' },

  // Memory detail
  backBtn:    { background: 'none', border: 'none', color: T.accent, fontFamily: T.body, fontSize: 15, cursor: 'pointer', padding: '0 0 16px', display: 'flex', alignItems: 'center', gap: 6 },
  detailCard: { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '20px 18px' },
  detailTitle:{ fontFamily: T.display, fontSize: 24, fontWeight: 400, color: T.ink, margin: '8px 0 10px', letterSpacing: -0.3, lineHeight: 1.15 },
  detailMeta: { display: 'flex', flexDirection: 'column', gap: 4, fontFamily: T.body, fontSize: 14, color: T.inkMute, marginBottom: 16 },
  detailSectionLabel:{ fontFamily: T.body, fontSize: 11, color: T.inkMute, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 },
  playBtn:    { background: T.ink, border: 'none', color: T.paper, borderRadius: T.radiusLg, padding: '12px 28px', fontFamily: T.body, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 14 },
  transcriptBox:  { background: T.paper, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', marginTop: 16 },
  transcriptText: { fontFamily: T.body, fontSize: 15, color: T.ink, lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' },
  deleteBtn:  { background: 'none', border: `1px solid ${T.divider}`, color: T.inkMute, padding: '10px 18px', borderRadius: T.radius, fontFamily: T.body, fontSize: 14, cursor: 'pointer', marginTop: 16 },

  // Index tab
  indexTab:       { flex: 1, padding: '10px 0', background: T.paperHigh, border: `1px solid ${T.divider}`, color: T.inkSoft, fontFamily: T.body, fontSize: 14, borderRadius: T.radius, cursor: 'pointer', fontWeight: 500 },
  indexTabActive: { background: T.ink, color: T.paper, border: `1px solid ${T.ink}` },
  indexList:  { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 },
  indexItem:  { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: T.body },
  indexName:  { fontFamily: T.display, fontSize: 17, color: T.ink, letterSpacing: -0.1 },
  indexCount: { fontFamily: T.body, fontSize: 13, color: T.accent, fontWeight: 600 },
  centerMsg:  { textAlign: 'center', fontFamily: T.body, color: T.inkMute, fontSize: 14, padding: '20px 0' },

  // Tree
  treePanel:  { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '18px 16px', marginTop: 16 },
  closeBtn:   { background: 'none', border: 'none', color: T.inkMute, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0 },
  actionBtn:  { background: T.paperHigh, border: `1px solid ${T.divider}`, color: T.ink, padding: '12px 16px', borderRadius: T.radius, fontFamily: T.body, fontSize: 14, cursor: 'pointer', textAlign: 'left', width: '100%' },
  suggestionCard:{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '12px', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start' },
  approveBtn: { background: T.tealSoft, border: `1px solid ${T.teal}`, color: T.teal, padding: '6px 12px', borderRadius: 99, fontFamily: T.body, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  dismissBtn: { background: 'none', border: `1px solid ${T.divider}`, color: T.inkMute, padding: '6px 12px', borderRadius: 99, fontFamily: T.body, fontSize: 12, cursor: 'pointer' },

  // Users tab
  addBtn:     { background: T.ink, border: 'none', color: T.paper, padding: '10px 18px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  userCard:   { background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, overflow: 'hidden' },
  badge:      { fontFamily: T.body, fontSize: 11, padding: '3px 9px', borderRadius: 99, fontWeight: 600 },
  editUserBtn:{ background: T.paperHigh, border: `1px solid ${T.divider}`, color: T.ink, padding: '7px 14px', borderRadius: T.radius, fontFamily: T.body, fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  deleteUserBtn:{ background: 'none', border: `1px solid ${T.divider}`, color: T.inkMute, padding: '7px 14px', borderRadius: T.radius, fontFamily: T.body, fontSize: 13, cursor: 'pointer' },
  formBox:    { background: T.paper, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '16px', marginBottom: 16 },
  formInput:  { display: 'block', width: '100%', background: T.paperHigh, border: `1.5px solid ${T.divider}`, color: T.ink, padding: '12px 14px', borderRadius: T.radius, fontFamily: T.body, fontSize: 15, outline: 'none', marginBottom: 10, boxSizing: 'border-box', transition: 'border-color 0.15s' },
  saveBtn:    { background: T.ink, border: 'none', color: T.paper, padding: '12px 20px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%' },
  cancelBtn:  { background: 'none', border: `1px solid ${T.divider}`, color: T.inkSoft, padding: '12px 20px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 15, cursor: 'pointer' },
  errorMsg:   { fontFamily: T.body, color: T.accent, fontSize: 13, marginBottom: 10 },
};
